'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const livretFeatures = [
{
  icon:
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
      </svg>,

  title: 'Une carte d\'accueil moderne',
  description: 'Un accueil chaleureux et personnalisé, disponible dès la confirmation de réservation.'
},
{
  icon:
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
      </svg>,

  title: 'Un guide pratique centralisé',
  description: 'Wifi, équipements, règles de séjour, contacts utiles — tout en un seul endroit.'
},
{
  icon:
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
      </svg>,

  title: 'Disponible 24h/24',
  description: "Vos voyageurs accèdent aux informations à n'importe quelle heure, sans vous déranger."
},
{
  icon:
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>,

  title: 'Moins de questions répétitives',
  description: 'Réduisez significativement les appels et messages pour des informations basiques.'
},
{
  icon:
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
      </svg>,

  title: 'Un accueil structuré',
  description: 'Organisez votre accueil une fois, pour tous vos voyageurs, de manière cohérente.'
}];


export default function LivretSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.animate-on-scroll')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="livret" ref={sectionRef} className="py-28 md:py-40 relative z-10 bg-background">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Section header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20">
          <div className="md:col-span-7 animate-on-scroll anim-in" style={{ animationPlayState: 'paused' }}>
            <span className="text-[10px] uppercase tracking-ultra text-accent font-medium block mb-5">
              Le livret numérique recommandé
            </span>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-tight text-foreground">
              Votre accueil,{' '}
              <span className="italic text-teal-gradient">réinventé.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end animate-on-scroll anim-in delay-100" style={{ animationPlayState: 'paused' }}>
            <p className="text-sm text-foreground-muted leading-relaxed">
              Le livret numérique Full-Loc est bien plus qu'un document. C'est votre assistant d'accueil permanent, 
              disponible pour chaque voyageur, à chaque instant.
            </p>
          </div>
        </div>

        {/* Main content: image + features */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Image block */}
          <div
            className="lg:col-span-5 relative animate-on-scroll anim-in delay-100"
            style={{ animationPlayState: 'paused' }}>
            
            <div className="relative">
              {/* Main image */}
              <div className="image-zoom aspect-[4/5] overflow-hidden border border-primary/15">
                <AppImage
                  src="https://images.unsplash.com/photo-1603923407797-2d25dfbbb1a0"
                  alt="Propriétaire consultant le livret numérique sur smartphone avec vue sur mer"
                  fill
                  className="object-cover" />
                
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-4 md:-right-8 glass-card p-5 shadow-lg float-anim">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 flex items-center justify-center rounded-none">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium text-foreground">Activation gratuite</p>
                    <p className="text-[10px] text-foreground-subtle">Propriétaires Anse des Rochers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Features list */}
          <div className="lg:col-span-7 space-y-0">
            {livretFeatures?.map((feature, i) =>
            <div
              key={feature?.title}
              className="animate-on-scroll anim-in group border-b border-primary/10 py-7 flex items-start gap-6 hover:bg-primary/2 transition-colors duration-300 px-2"
              style={{ animationPlayState: 'paused', animationDelay: `${(i + 1) * 0.08}s` }}>
              
                <div className="mt-0.5 text-primary shrink-0 group-hover:text-primary-dark transition-colors">
                  {feature?.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-medium text-foreground text-base mb-2">{feature?.title}</h3>
                    <span className="text-[9px] uppercase tracking-ultra text-foreground-subtle shrink-0 mt-1">
                      0{i + 1}
                    </span>
                  </div>
                  <p className="text-sm text-foreground-muted leading-relaxed">{feature?.description}</p>
                </div>
              </div>
            )}

            {/* CTA note */}
            <div className="pt-10 animate-on-scroll anim-in" style={{ animationPlayState: 'paused' }}>
              <div className="flex items-center gap-4 p-6 border border-accent/25 bg-accent/5">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-accent shrink-0">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Activation <span className="text-accent">100% gratuite</span> pour les propriétaires Anse des Rochers
                  </p>
                  <p className="text-xs text-foreground-muted mt-1">
                    Dans le cadre du partenariat officiel Livret Vacances Guadeloupe × Anse des Rochers
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}